/**
 * What's New release notes — content + contract.
 *
 * 📖 FULL DOCS: wiki/Release-Notes.md (runbook, authoring rules,
 *               architecture, analytics taxonomy).
 *
 * RESPONSIBILITY
 *   Source-of-truth list of every release entry shown in the
 *   "What's new" panel. Entries live in code so a release note ships
 *   in the same PR as the feature itself — no CMS / DB / authoring
 *   surface to wire up.
 *
 * SHIP A RELEASE ENTRY ALONGSIDE EVERY MEANINGFUL FEATURE.
 *   Prepend a new entry to RELEASES below, in the same PR as the
 *   feature itself. A feature without an entry is silently invisible
 *   to everyone who already finished the welcome tour.
 *
 * AUTHORING RULES
 *   - Newest first. Editors append to the TOP of RELEASES.
 *   - id MUST be `r-<YYYY-MM-DD>-<kebab-slug>` so they sort lexically
 *     by date (drives the "is this newer than what I last saw?" check
 *     in ReleasesService.isUnread).
 *   - Use British English in user-facing strings (colour, customise,
 *     organisation). No emoji.
 *   - Highlights: 3–6 bullets. Title is short + imperative; text is one
 *     sentence. Reuse icon names from icon-registry (mic, folder,
 *     sparkles, recurring, plug, bot, brain, etc.).
 *   - Media is optional and absent today (internal beta). When we
 *     start adding screenshots / Loom clips, see ReleaseMedia for
 *     supported shapes.
 *
 * FUTURE MIGRATION
 *   When PMs/marketing want to publish without a deploy, swap this
 *   constant for a service that fetches a backend collection. The
 *   ReleaseEntry shape stays identical so UI consumers don't change.
 */

/** Optional media slot. YouTube IDs are stored, not URLs, so the
 *  panel can render the privacy-mode embed (youtube-nocookie) without
 *  third-party cookies. Self-hosted videos use 'video' + absolute URL. */
export interface ReleaseMedia {
  kind: 'image' | 'youtube' | 'video';
  /** For 'youtube': the 11-char video ID. For 'image' / 'video': absolute URL. */
  src: string;
  alt?: string;
  /** Optional caption rendered below the media. */
  caption?: string;
}

export interface ReleaseLink {
  label: string;
  url: string;
  /** Icon name from icon-registry. Defaults to 'external-link'. */
  icon?: string;
}

export interface ReleaseHighlight {
  /** Short imperative title — e.g. "Project chips on every meeting". */
  title: string;
  /** One-sentence body. */
  text: string;
  /** Optional icon name from the registry. */
  icon?: string;
}

export interface ReleaseEntry {
  /** Stable id used for seen-state tracking. Convention:
   *  `r-<YYYY-MM-DD>-<slug>` so they sort lexically by date. */
  id: string;
  /** Display-only version label — e.g. '1.4'. Optional today. */
  version?: string;
  /** ISO date 'YYYY-MM-DD'. Drives the date pill + sort order. */
  date: string;
  /** Headline — sentence-case, no trailing full stop. */
  title: string;
  /** Single-paragraph blurb under the title. */
  summary: string;
  highlights: ReleaseHighlight[];
  media?: ReleaseMedia[];
  links?: ReleaseLink[];
  /** Tag chips — colour-coded surface area ("Flow", "Maps", "AI"). */
  tags?: string[];
  /** Optional gating. 'admin' hides the entry from non-admin users.
   *  Defaults 'all' when absent. */
  audience?: 'all' | 'admin';
}

/**
 * Release feed — newest first. Append new entries to the TOP.
 *
 * Internal beta seed (May 2026) — four entries spanning the most
 * recent meaningful work so the panel feels alive when an existing
 * user opens it for the first time. None of these have media; we
 * start adding screenshots once the desktop-app rollout opens up.
 */
export const RELEASES: readonly ReleaseEntry[] = [
  {
    id: 'r-2026-05-20-e-flow-attach-meetings',
    date: '2026-05-20',
    title: 'Flow — attach a recording to any meeting',
    summary:
      'Flow now sits properly inside the meeting workflow. Attach any recording ' +
      'to an existing meeting from the session card or detail page, drop files ' +
      'or paste a transcript with the meeting picker built in, and explore the ' +
      'redesigned /flow home — greeting band, glance ribbon, quick-actions strip.',
    highlights: [
      {
        icon: 'plug',
        title: 'Attach to existing meeting',
        text: 'A new attach affordance lives on every session card and detail page; the picker portals via a CDK overlay so it sits cleanly above the card and is keyboard-dismissible.',
      },
      {
        icon: 'upload',
        title: 'Ingest a recording in three steps',
        text: 'The /flow/ingest page refresh puts files at the top, lays out Step 1 / 2 / 3, gates submit until each step is satisfied, and supports paste-text as a first-class mode alongside the dropzone.',
      },
      {
        icon: 'layout',
        title: 'New Flow home',
        text: 'Greeting band, glance ribbon, quick-actions strip, equalised project card heights and a compact search pill replace the wide search input — the home reads cleanly at every viewport.',
      },
      {
        icon: 'edit',
        title: 'Real block editor inside Flow',
        text: 'A TipTap-powered block editor with selection toolbar replaces six legacy block types (text, heading, quote, callout, todo, toggle) — formatting, lists and inline marks land where you expect them.',
      },
      {
        icon: 'folder',
        title: 'Knowledge lives under Flow',
        text: 'Knowledge pages now mount inside the Flow shell so navigation between projects, sessions and knowledge keeps the same chrome.',
      },
      {
        icon: 'layers',
        title: 'Rail polish',
        text: 'Workspace switcher and page tree match the nav row style; knowledge navigation no longer flickers on cold-load; the rail behaviour is consistent across every Flow page.',
      },
    ],
    tags: ['Flow', 'New'],
  },
  {
    id: 'r-2026-05-20-d-flow-intelligence',
    date: '2026-05-20',
    title: 'Flow Intelligence — shared chat primitives, citations, suggestions',
    summary:
      'The Flow Intelligence chat rail and the Workplace AI panel now share a single ' +
      'set of chat primitives so they behave identically — same bubble, same input, ' +
      'same suggestion handling. Plus citation chips, a context ribbon, and ' +
      'smarter suggestion-chip semantics.',
    highlights: [
      {
        icon: 'sparkles',
        title: 'Shared im-chat-* primitives',
        text: 'Frame, scroller, input, suggestions and bubbles are now shared components consumed by every chat surface (Flow Intelligence, AI panel, Workplace AI) so polish lands everywhere at once.',
      },
      {
        icon: 'link',
        title: 'Citation chips on every answer',
        text: 'Answers cite their source recordings, projects and knowledge pages inline; click-through opens the cited surface in a side panel.',
      },
      {
        icon: 'rating',
        title: 'Suggestion chips behave properly',
        text: 'Chips ending in "…" now pre-fill the input rather than firing immediately; the picker dismisses on outside click and Escape.',
      },
      {
        icon: 'eye',
        title: 'Context ribbon',
        text: 'A ribbon above the chat shows the live context — which project, session or filters the AI is grounded in — so answers are auditable at a glance.',
      },
      {
        icon: 'settings',
        title: 'Scoped to Flow',
        text: 'The Flow Intelligence trigger and panel mount only on /flow routes, so the rest of the app keeps its own AI affordances.',
      },
    ],
    tags: ['Flow', 'AI'],
  },
  {
    id: 'r-2026-05-20-c-meeting-panel-parity',
    date: '2026-05-20',
    title: 'Meeting panel — view-mode parity, multi-room, field parity',
    summary:
      'Open any existing meeting and the panel now reads as fully as it writes — ' +
      'section icons, availability, agenda, private notes and Flow attachment are ' +
      'all live in view-mode. Multi-room bookings get a header badge and per-room ' +
      'confirmation list; every Schedule V2 field round-trips end-to-end.',
    highlights: [
      {
        icon: 'eye',
        title: 'View-mode parity',
        text: 'Phase 1 brings section icons, availability and the agenda surface into view-mode; subsequent waves added private notes (W5), Flow attachment from view-mode via the viewer overlay (W6) and a createCompanionBooking wrapper (W8).',
      },
      {
        icon: 'layers',
        title: 'Multi-room polish',
        text: 'The header wears a room-count badge, the confirmation shows a per-room list, and the schedule nudges per room — so a multi-room booking reads as one meeting with N rooms, not N parallel meetings.',
      },
      {
        icon: 'calendar',
        title: 'Schedule V2 field parity',
        text: 'Every Schedule V2 field is sent on submit and received on edit — bookings created in V1 and V2 now round-trip the same shape.',
      },
      {
        icon: 'recurring',
        title: 'Submit + autosave polish',
        text: 'Submit flushes any pending autosave and sweeps orphan drafts; the confirm-on-close dialog dedupes autosave duplicates; declined meetings are hidden from My Day / My Week timelines; bookingForEditing surfaces a clear error when the meeting isn\'t reachable.',
      },
      {
        icon: 'plug',
        title: 'Mounted once at the shell',
        text: 'The panel mounts a single time at the app shell, so the "open meeting" chip works from any surface — viewer, schedule, home — without re-instantiating the heavy panel state.',
      },
    ],
    tags: ['Schedule', 'BMP'],
  },
  {
    id: 'r-2026-05-20-b-workplace-defaults',
    date: '2026-05-20',
    title: 'Workplace Paper defaults + shell polish',
    summary:
      'A fresh install now lands on the canonical Ricoh look — Workplace Paper ' +
      'theme, Inter font, Workplace Ink brand colour — and a handful of long-' +
      'standing chrome niggles got fixed: the profile side panel reads more ' +
      'cleanly, the spaces widget fits its slot, and the admin rail expand toggle works.',
    highlights: [
      {
        icon: 'palette',
        title: 'Workplace Paper is the new default',
        text: 'New users land on Workplace Paper (light) with Inter and Workplace Ink as the brand colour. Existing preferences are preserved — change yours any time from /profile/appearance.',
      },
      {
        icon: 'layout',
        title: 'Profile side panel widened',
        text: 'The right-hand profile panel is now a fluid 400–480px (was a fixed 380px). Meeting titles, schedule rows and the day-at-a-glance bullets no longer truncate.',
      },
      {
        icon: 'monitor',
        title: 'Spaces widget fits its slot',
        text: 'The empty-state hero and four action tiles (Quick Book / Ask AI / Open Map / Set Status) are now sized to fit at the widget\'s default 3×4 height — no more clipped tiles.',
      },
      {
        icon: 'settings',
        title: 'Admin rail fixes + polish',
        text: 'The expand toggle now works on detail pages (was pinned collapsed). Labels left-align next to icons. Row height, group spacing and group headings tightened for a calmer rhythm.',
      },
      {
        icon: 'bell',
        title: 'Building alerts polish',
        text: 'Phase 4 brings per-viewer dismissals, time-zone hints on alert windows and shell-level banner styling so an active alert reads consistently across the home, viewer and meeting panel.',
      },
      {
        icon: 'layers',
        title: 'Bottom nav rebrand',
        text: 'The bottom navigation\'s Projects entry is now Flow, with a lens-blur icon, route-aware active state and a brand-coloured accent.',
      },
    ],
    tags: ['Shell', 'Theme'],
  },
  {
    id: 'r-2026-05-20-a-foundations',
    date: '2026-05-20',
    title: 'Foundations — privacy, accessibility, analytics',
    summary:
      'Quality-of-life under the hood — a new privacy toggle for beta comms, ' +
      'a long-standing accessibility crash fixed in the radio primitive, and ' +
      'richer product-analytics signals so the team can track beta usage.',
    highlights: [
      {
        icon: 'shield',
        title: 'Beta communications privacy toggle',
        text: 'A new betaCommunications switch on /profile/privacy lets you opt in or out of beta-only emails; the preference syncs to Brevo as a person property.',
      },
      {
        icon: 'edit',
        title: 'Privacy page polish',
        text: 'Booking-card text is anchored to its icon, toggle switches are now visibly distinct in light themes, and the layout reads cleanly on narrower viewports.',
      },
      {
        icon: 'star',
        title: 'Radio accessibility fix',
        text: 'im-radio no longer crashes with NG0950 when a required input lands in an in-progress form — pickers using radio groups are now safe.',
      },
      {
        icon: 'rating',
        title: 'Richer product analytics',
        text: 'PostHog identify is enriched with customer group + beta-tester flags; feature-flag exposures and release annotations land in the analytics stream alongside structured $exception events.',
      },
    ],
    tags: ['Privacy', 'Foundations'],
  },
  {
    id: 'r-2026-05-19-o-bmp-redesign',
    date: '2026-05-19',
    title: 'Book Meeting Panel, redesigned end-to-end',
    summary:
      'A faster, smarter way to book meetings. Save your favourite formats as templates, ' +
      'see when everyone is free at a glance, and let AI draft the title and agenda for ' +
      'you — all from a single panel that you can minimise and pick back up later.',
    highlights: [
      {
        icon: 'folder',
        title: 'Templates row',
        text: 'Save any meeting as a personal template and hydrate new drafts in one click from the top of the Meeting tab.',
      },
      {
        icon: 'layers',
        title: 'Inline layout picker on every room',
        text: 'Swap between Boardroom, Theatre, Classroom, U-shape, Standing or As-is from a schematic popover; the picker shows seat deltas (e.g. "17 (+8)") and setup/reset time.',
      },
      {
        icon: 'calendar',
        title: 'Find-a-time strip',
        text: 'Per-attendee availability lanes with the proposed-meeting window rendered as a green box, conflict bands ringed in warning, and "better times nearby" pills.',
      },
      {
        icon: 'sparkles',
        title: 'AI side rail',
        text: 'Suggest a title, draft an agenda, summarise the last meeting — every suggestion lands as a card with explicit Use and Dismiss actions.',
      },
      {
        icon: 'recurring',
        title: 'Drafts dock',
        text: 'Minimise any in-progress meeting; chips collect bottom-right (12 max, FIFO) so you can switch context and restore later with one click.',
      },
      {
        icon: 'plug',
        title: 'Per-room catering with running total',
        text: 'Stack multiple vendor orders against a single room with add and remove controls and a live total.',
      },
    ],
    tags: ['BMP', 'New'],
  },
  {
    id: 'r-2026-05-19-n-bmp-ai',
    date: '2026-05-19',
    title: 'Plan with AI — the meeting panel learns your context',
    summary:
      'A dedicated AI side rail and a Smart Assist intent chip help you write the agenda, ' +
      'shape the title, recap the last meeting, and reuse the rooms your team already ' +
      "loves. Every suggestion is one-click accept or dismiss — nothing happens behind your back.",
    highlights: [
      {
        icon: 'sparkles',
        title: 'Draft an agenda (with optional objective)',
        text: 'One click produces a structured agenda from the title, attendees, notes and any attached Flow project. Open the chevron to add an objective — "Decide on the Q3 roadmap" — and the agenda is shaped around it.',
      },
      {
        icon: 'edit',
        title: 'Suggest a title',
        text: 'Type a few attendees and a one-line title is generated and offered both as an inline chip under the subject input and as a card in the AI rail. Accept writes it through; Dismiss never asks again for this draft.',
      },
      {
        icon: 'recurring',
        title: 'Summarise last meeting + suggested follow-on agenda',
        text: 'When at least one attendee or Flow project is attached, recap your most recent related meeting and get a suggested follow-on agenda you can prepend with one click.',
      },
      {
        icon: 'rating',
        title: 'Similar past meetings + rooms this group uses',
        text: 'Two auto-loading cards show the top 3 historically similar meetings (one click copies title + duration) and the top 3 rooms you usually book with these people (one click sets the primary room).',
      },
      {
        icon: 'star',
        title: 'Smart Assist intent chip',
        text: 'Classifies the meeting from the title — interview, sales call, retro, 1:1, all-hands — and offers the right duration and space type for that intent. Local-only, no network.',
      },
      {
        icon: 'sparkles',
        title: 'AI Briefing pane',
        text: 'A condensed Haiku-written prep brief pulls attendees, notes, Flow project context and similar meetings into one panel — handy right before you walk in.',
      },
    ],
    tags: ['BMP', 'AI', 'New'],
  },
  {
    id: 'r-2026-05-19-m-schedule-v2',
    date: '2026-05-19',
    title: 'Schedule V2 — streamlined room scheduling',
    summary:
      'A redesigned schedule view with multi-location support, attendee availability bands ' +
      'and one-click booking from any empty slot. Every chrome surface — top bar, filter ' +
      'strip, schedule blocks — was rebuilt to read clearly and behave consistently.',
    highlights: [
      {
        icon: 'calendar',
        title: 'New top command bar',
        text: 'Date stepper, multi-location picker, people picker, view tabs, Filters and Find a Time in one slim glass bar.',
      },
      {
        icon: 'settings',
        title: 'Multi-location persistence',
        text: 'Selected buildings survive reloads; floor headers wear a building pill when more than one location is active.',
      },
      {
        icon: 'rating',
        title: 'Per-person bands and common-free banner',
        text: 'Add attendees and the grid renders their busy lanes plus a banner spotlighting windows everyone is free.',
      },
      {
        icon: 'sparkles',
        title: 'Amenity dots on every room',
        text: 'Whiteboard, camera, phone, cast and display icons sit on each room row with +N overflow when a room has more.',
      },
      {
        icon: 'star',
        title: 'Needs-a-room smart strip',
        text: 'Meetings without a room float to a smart strip with Auto-book and Pick room actions, so nothing falls through the cracks.',
      },
      {
        icon: 'edit',
        title: 'Bottom book sheet',
        text: 'Click any empty slot for a focused booking sheet pinned to the bottom centre with −15m and +15m duration controls.',
      },
    ],
    tags: ['Schedule', 'New'],
  },
  {
    id: 'r-2026-05-19-l-schedule-planner',
    date: '2026-05-19',
    title: 'Schedule planner — drag, find, gap-fill',
    summary:
      'Power-user gestures and a dedicated Find Space slide-in for when a click on an empty ' +
      'slot is not enough. Drag to resize, drag to create, hover for an instant time preview, ' +
      'and let the planner surface the best slots when several people need to be in the room.',
    highlights: [
      {
        icon: 'edit',
        title: 'Drag-to-resize selection slot',
        text: 'Left/right edge handles let you stretch a slot, drag the middle to reposition; the time badge updates in real time.',
      },
      {
        icon: 'rating',
        title: 'Full-height time crosshair',
        text: 'Hover anywhere on the grid for a vertical guide and instant time label; with attendees added, the badge flips to "All free" or "Conflict".',
      },
      {
        icon: 'sparkles',
        title: 'Find Space slide-in',
        text: 'A right-side panel ranks the best time slots + matching rooms across one day or several, with duration presets (15/30/45/60/90/120), attendee picker and inline booking.',
      },
      {
        icon: 'layers',
        title: 'Available-slot + gap-finder highlights',
        text: 'While dragging, free intervals light up and gap-filling suggestions float in so you stop fighting the calendar.',
      },
      {
        icon: 'star',
        title: 'Density variants on every block',
        text: 'Switch the grid between condensed (title-only), standard, and extended (3-row with organiser) via Filters → Density. Past, pending and recurring meetings each get their own visual treatment with inline icons.',
      },
      {
        icon: 'eye',
        title: 'Schedule legend',
        text: 'Toggleable legend bar maps every swatch — Yours, Busy, Tentative, External, Out of office, Recurring — so colour-coding is decoded at a glance.',
      },
    ],
    tags: ['Schedule', 'New'],
  },
  {
    id: 'r-2026-05-19-k-meeting-detail',
    date: '2026-05-19',
    title: 'Meeting Detail — a richer view of every meeting',
    summary:
      "Open an existing meeting and the panel now reads like a properly designed detail page " +
      "— hero with copy-share-link, an inline RSVP card, the join button, every Flow recording " +
      'with transcript, AI insights and notes, and any building alerts that overlap the slot.',
    highlights: [
      {
        icon: 'edit',
        title: 'Hero with copy-share-link',
        text: 'Eyebrow, large subject, category chip and calendar / clock / map-pin meta line. Copy meeting share-link or Meeting ID with one click (the icon flips to a check on success).',
      },
      {
        icon: 'rating',
        title: 'RSVP card — Yes, Maybe, No',
        text: 'Tri-state response card right at the top for viewers (hidden for organisers); the pill tone reflects your current status.',
      },
      {
        icon: 'plug',
        title: 'Join card with passcode copy',
        text: 'Teams or Zoom join button, plus copy Meeting ID and copy passcode — no more digging through the calendar invite for the dial-in.',
      },
      {
        icon: 'mic',
        title: 'Per-recording reveals',
        text: 'Every Flow card has expandable Transcript, AI insights and Notes panes. The transcript pane searches inline with highlight; speakers can be renamed via attendee + directory typeahead; m:ss timestamps deep-link to the moment.',
      },
      {
        icon: 'recurring',
        title: 'Cross-recording continuity surface',
        text: 'When a meeting is part of a recurring series or attached to a project, sibling meetings appear right on the card with click-through.',
      },
      {
        icon: 'bell',
        title: 'Building alerts overlay',
        text: 'If an active alert covers the room or floor during the meeting window, it banners at the top of the panel so you know before you walk in.',
      },
      {
        icon: 'settings',
        title: 'Edit / Cancel with delta-only saves',
        text: 'View ↔ Edit toggle keyed off your permissions; saves send only the fields you changed and rebase the snapshot once the calendar service confirms.',
      },
    ],
    tags: ['Schedule', 'BMP'],
  },
  {
    id: 'r-2026-05-19-j-my-day-week',
    date: '2026-05-19',
    title: 'My Day, My Week, Tasks and the Daily Planning Ritual',
    summary:
      'The personal command centre got a coordinated upgrade — AI day summaries, highlight ' +
      'chips that spotlight whatever needs your attention, a travel card for commute context, ' +
      'a 4-step morning planning ritual, and a complete task system with Kanban + List + Timer.',
    highlights: [
      {
        icon: 'sparkles',
        title: 'AI day summary',
        text: 'A one-line headline plus two callouts up top — "Two back-to-backs · 90 min focus block at 14:00" — generated fresh from your real day, not a template.',
      },
      {
        icon: 'star',
        title: 'Highlight chips',
        text: 'Critical / warning / info / suggestion chips surface back-to-backs, no-break days, overload, missing prep — with +N overflow when there are too many to show.',
      },
      {
        icon: 'map',
        title: 'Travel card for commute context',
        text: 'A day-travel card surfaces the right commute mode for your first meeting (drive / walk / cycle / transit) with an ETA based on your home address.',
      },
      {
        icon: 'edit',
        title: 'Daily Planning Ritual',
        text: 'A 4-step morning modal: Yesterday Review (done / defer / cancel), Today\'s Suggestions, Workload Check with capacity bar, and Finalise — focus-trapped, Escape to dismiss.',
      },
      {
        icon: 'folder',
        title: 'Task system — Kanban, List, Timer',
        text: 'Switch between a Kanban board and a list view, add quick tasks inline, open a full task dialog, run a Pomodoro-style task timer, and filter by source (calendar / Linear / habit / inbox).',
      },
      {
        icon: 'recurring',
        title: 'Habits and My Week extras',
        text: 'Protect recurring personal time (lunch, exercise, deep work) as habits. My Week persists hide-days, lays out conflicts in multi-column, and shows work-status short labels (Office / Remote / Break / OOO / Sick) plus a calendar-reachability badge.',
      },
    ],
    tags: ['Schedule', 'AI'],
  },
  {
    id: 'r-2026-05-19-i-flow-shell',
    date: '2026-05-19',
    title: 'Flow — a proper home for every recording',
    summary:
      "Flow now has its own product shell with a left rail, a centre that switches between " +
      "projects and sessions, and a persistent AI chat rail on the right. Every recording is " +
      'a first-class page with transcript search, segment deep-links and inline speaker renaming.',
    highlights: [
      {
        icon: 'layers',
        title: '3-column Flow shell at /flow',
        text: 'Collapsible left rail (240/56px, auto-collapses below 1080px) with project + session nav and count chips, centre router outlet, persistent AI chat rail on the right. Rail state and chat-open are serialised to ?rail= and ?chat=.',
      },
      {
        icon: 'star',
        title: 'Quick filters in the rail',
        text: 'Active / Archived status and Mine / Shared with me membership chips filter both Projects and Sessions; "Recent projects" surfaces your last five with an active-row accent.',
      },
      {
        icon: 'mic',
        title: 'Deep-linkable session pages',
        text: '/flow/sessions/:id renders a hero with subject + date + duration + meaningful-speaker count (≥5 segments or ≥10s, or renamed), then summary → topics → AI insights → speakers → transcript → notes. Share a link with ?seg=, ?transcriptQ= or ?speaker= and the page jumps straight there.',
      },
      {
        icon: 'edit',
        title: 'Speaker rename + dedupe',
        text: 'Diarised speakers can be renamed inline via attendee + directory typeahead (debounced 180ms); the writes structure assignedUserId + assignedUserEmail so the backend clusters voice embeddings against real identities over time and dedupes parallel @ricoh-europe vs @ricohspaces records.',
      },
      {
        icon: 'sparkles',
        title: 'Persistent AI chat rail',
        text: 'Streaming chat (via flowAssistantStream WS subscription) scoped to the page — all of /flow, a single project, or a single session. Context picker popover attaches project / session / Knowledge page / person refs as chips; threads list per viewer; deep-link via ?thread=; per-thread delete.',
      },
      {
        icon: 'eye',
        title: 'Recover missing recordings',
        text: 'Dedicated /flow/sessions list with All / In a project / Standalone filters and a "Recover missing" action that reconciles orphaned recordings against the meeting catalogue (reconcileMyFlowOrphans).',
      },
      {
        icon: 'plug',
        title: 'Add to Knowledge from any session',
        text: 'A new affordance turns any session\'s enhanced notes into a Knowledge page in one click. Markdown rendering is sanitised on the way out.',
      },
    ],
    tags: ['Flow', 'AI'],
  },
  {
    id: 'r-2026-05-19-h-flow-projects',
    date: '2026-05-19',
    title: 'Flow Projects + Knowledge — one connected thread',
    summary:
      'Projects organise a recurring series or a multi-meeting initiative into one story. ' +
      'A new AI rollup hero synthesises across every meeting in the project, suggested-project ' +
      'pills attach orphans automatically, and projects can be linked to a Knowledge workspace ' +
      'so notes and recordings live side-by-side.',
    highlights: [
      {
        icon: 'folder',
        title: 'Project detail at /flow/projects/:id',
        text: '?section=rollup|sessions|knowledge anchors scroll to each section; left side shows the project hero, right side opens a focused detail panel for one session at a time.',
      },
      {
        icon: 'sparkles',
        title: 'AI rollup hero',
        text: 'A Haiku-synthesised summary of every meeting in the project (decisions, recurring themes, next steps), with a manual Refresh button and a "Updated 5m ago" freshness label.',
      },
      {
        icon: 'rating',
        title: 'Suggested-project pill on session cards',
        text: 'Fuzzy match on title tokens + recency surfaces a "Suggested: <Project> → Attach" pill on standalone sessions so orphans never linger.',
      },
      {
        icon: 'recurring',
        title: 'Recurring-series back-fill',
        text: 'Tag a recurring 1:1 into a project and opt in — every prior occurrence joins automatically; future ones auto-attach as they arrive.',
      },
      {
        icon: 'plug',
        title: 'Project ↔ Knowledge integration',
        text: 'Link any project to a Knowledge workspace; the detail page rolls up recent pages with mic vs page icons (Flow-derived vs hand-written) and Flow cards route straight to the right folder.',
      },
      {
        icon: 'settings',
        title: 'Flow Cost admin console',
        text: 'Admin-only /flow/admin/costs page shows daily AI spend buckets (7 / 30 / 90 days) with a scope-flip indicator for viewer-only fallback.',
      },
    ],
    tags: ['Flow', 'AI'],
  },
  {
    id: 'r-2026-05-19-g-design-system-v2',
    date: '2026-05-19',
    title: 'Workplace Paper — the new design system',
    summary:
      "Eight themes named after papers; your division's brand colour is the ink printed on " +
      'top. The compact in-app picker shows live mini-mocks before you commit, and a brand-test ' +
      'matrix lets admins stress-test how any brand looks on every paper.',
    highlights: [
      {
        icon: 'sparkles',
        title: '8 themes, each a paper',
        text: 'Light: Warm, Pure, Workplace paper, Current light. Dark: Midnight, Black, Workplace slate, Current dark. Pick from /profile/appearance or the compact picker in the profile side panel.',
      },
      {
        icon: 'edit',
        title: '4 fonts',
        text: 'Graphik (default), Inter, Editorial and Museo — pair with any theme.',
      },
      {
        icon: 'rating',
        title: 'Workplace Ink is the default ink',
        text: '#1E2A3A replaces the old Indigo as the default brand swatch — paper-first, ink-second, true to the Ricoh print heritage.',
      },
      {
        icon: 'star',
        title: 'Compact picker with live mini-mocks',
        text: 'Every theme tile and font tile renders a live mini-mock of its surfaces so you can preview the look without committing — hover for the descriptor, click to apply.',
      },
      {
        icon: 'layers',
        title: 'Brand-test matrix',
        text: '/settings/brand-test now offers two view modes — single-brand comparison and a full theme × brand matrix grid for visual regression checks across every paper.',
      },
      {
        icon: 'settings',
        title: 'Tokens, not hardcodes',
        text: 'The hex-literal lint baseline dropped 28% (577 → 414) as widgets and settings migrated to design tokens. Themes flip cleanly everywhere.',
      },
    ],
    tags: ['Design system', 'Themes'],
  },
  {
    id: 'r-2026-05-19-f-wcag-hardening',
    date: '2026-05-19',
    title: 'Accessibility, high-contrast and theme hardening',
    summary:
      'Every theme now passes WCAG-AA in the gallery audit. When Teams is in high-contrast ' +
      'mode the app honours it automatically, and on light themes we now adapt to the OS ' +
      "prefers-contrast: more setting. A dedicated in-app High-contrast toggle is the next " +
      'step.',
    highlights: [
      {
        icon: 'shield',
        title: 'Teams high-contrast overlay',
        text: 'When the app runs inside Microsoft Teams with high-contrast active, [data-teams-theme="contrast"] flips bg/text/borders/availability colours to the WCAG AAA palette automatically.',
      },
      {
        icon: 'eye',
        title: 'OS prefers-contrast (light themes)',
        text: '@media (prefers-contrast: more) bumps glass opacity to 92%, darkens borders and strengthens secondary/tertiary text on every light paper — no setting to flip.',
      },
      {
        icon: 'sparkles',
        title: 'AI gradient is WCAG-AA everywhere',
        text: 'Dedicated --ai-gradient tokens fix the dark-theme readability gap so AI buttons stay legible on every paper.',
      },
      {
        icon: 'rating',
        title: 'Intent-aware contrast matrix',
        text: 'The gallery now scores body, large-text and graphic pairs against the right WCAG threshold — no more false fails for brand or status colours.',
      },
      {
        icon: 'layers',
        title: 'Availability + sunken wells fixed',
        text: 'Booked / available / limited tints render correctly on every paper, and recessed surfaces and schedule blocks pick up the right tint per theme.',
      },
      {
        icon: 'settings',
        title: 'Zero critical WCAG issues on /design-system',
        text: 'An axe-core gate runs across 20 theme × section permutations on every push; any regression fails the build.',
      },
      {
        icon: 'recurring',
        title: 'Coming next: a manual high-contrast toggle',
        text: 'A dedicated in-app toggle (auto / standard / high) is in design so you can force the high-contrast palette outside Teams too — opt-in via /profile/appearance.',
      },
    ],
    tags: ['Accessibility', 'Design system'],
  },
  {
    id: 'r-2026-05-19-e-widgets',
    date: '2026-05-19',
    title: '24 new widgets and a responsive grid',
    summary:
      'Nine live-data widgets and fifteen preview widgets join the dashboard, plus a ' +
      'responsive grid that reflows widgets rather than crushing them. Every action routes ' +
      'through one framework so analytics, toasts and cross-widget refreshes stay consistent.',
    highlights: [
      {
        icon: 'layers',
        title: '9 live-data widgets',
        text: 'Weather, Commute, Air Quality, Reading List, App Spotlight, Connected Apps, Live Map Pin, Recent People and This Week (bookings strip).',
      },
      {
        icon: 'star',
        title: '15 preview widgets with synthetic data',
        text: 'Birthdays, New Starters, Cafeteria Menu, Building Alerts, Time Off, Expenses, Learning, Goals, Sustainability, Office Amenities, Pulse Polls, Announcements, Notifications Hub, Wellbeing, Team Pulse — each tagged with a "Preview" pill so it is clear which data is real.',
      },
      {
        icon: 'recurring',
        title: 'Responsive widget grid',
        text: 'Widgets reflow to the next row as the viewport narrows — a 6+3+3 row collapses to a full-width lead with meeting and spaces side-by-side underneath at 1200px.',
      },
      {
        icon: 'sparkles',
        title: 'Brand-tinted empty states',
        text: 'Six widgets gained branded empty cards with clear CTAs — connected-apps, integration-card, commute, weather, air-quality and office-today.',
      },
      {
        icon: 'edit',
        title: 'Collision-free keyboard shortcuts',
        text: 'Eight core widgets (quick-book, spaces, check-in, meeting-next, reading-list, weather, commute, air-quality) get focus-scoped shortcuts that can safely share keys.',
      },
      {
        icon: 'plug',
        title: 'One action framework',
        text: 'Every widget routes its bookings actions through the same service for uniform analytics, toasts and cross-widget refresh hints.',
      },
    ],
    tags: ['Widgets'],
  },
  {
    id: 'r-2026-05-19-d-building-alerts',
    date: '2026-05-19',
    title: 'Building Alerts — schedule, target, broadcast',
    summary:
      'A brand-new admin section for scheduling alerts at any level of the building hierarchy. ' +
      'Mirror them into the homepage feed so employees see them without opening Admin; block ' +
      'bookings during the alert window when needed.',
    highlights: [
      {
        icon: 'settings',
        title: 'New admin section at /settings/alerts',
        text: 'Create, schedule and target alerts by building, floor, space or asset, with multi-target closures and reminder cadence.',
      },
      {
        icon: 'star',
        title: 'Three severities',
        text: 'Info, warning and urgent — each with its own visual treatment across the homepage widget, the space-detail panel and meeting cards.',
      },
      {
        icon: 'newspaper',
        title: 'Article feed mirror',
        text: 'Opt-in notify flag publishes the alert into the homepage feed so employees see it without ever opening Admin.',
      },
      {
        icon: 'recurring',
        title: 'Block-bookings flag',
        text: 'Prevent new bookings during the alert window — useful for planned outages, deep cleans or floor closures.',
      },
      {
        icon: 'sparkles',
        title: 'Live surfaces everywhere',
        text: 'Alerts render in the homepage widget, on the space-detail panel and on individual meeting cards via the shared alerts banner.',
      },
    ],
    tags: ['Admin', 'New'],
  },
  {
    id: 'r-2026-05-19-c-settings-admin',
    date: '2026-05-19',
    title: 'Settings and Admin overhaul',
    summary:
      'A new Activity Console for audit and compliance, an Organisation Settings shell that ' +
      'unifies tenant-wide settings, a Workplace AI helper docked on every page, and a ' +
      'one-stop content table covering every article, announcement and video.',
    highlights: [
      {
        icon: 'rating',
        title: 'Activity Console at /settings/activity',
        text: 'SOC 2 / ISO 27001 audit log with Feed, Dashboard, Anomalies, tamper-evident Chain and NDJSON / CSV export.',
      },
      {
        icon: 'settings',
        title: 'Organisation Settings shell',
        text: 'Secondary rail covering Calendar Sync, Branding and Feature Flags — each with its own dirty detection and floating save bar.',
      },
      {
        icon: 'sparkles',
        title: 'Workplace AI admin helper',
        text: 'Slide-out chat panel docked to every settings page with suggestion chips and the ability to navigate the admin from chat. State survives refresh.',
      },
      {
        icon: 'star',
        title: 'Admin search (⌘P)',
        text: 'Floating combobox that fuzzy-matches admin sections by label, description, URL or keyword, with full keyboard navigation.',
      },
      {
        icon: 'edit',
        title: 'Content Management at /settings/content',
        text: "A bird's-eye table for every article, announcement and video the tenant has created, with server-side paging, status and type filters and bucket counts.",
      },
      {
        icon: 'layers',
        title: 'Map Ingest, end-to-end',
        text: 'Library / Upload tabs, 2D + 3D Three.js preview, AI critique drawer, PDF support and a 4-step upload wizard ending in "Assign to location + save".',
      },
    ],
    tags: ['Admin'],
    audience: 'admin',
  },
  {
    id: 'r-2026-05-19-b-calendar-sync',
    date: '2026-05-19',
    title: 'Calendar sync monitoring + Integrations',
    summary:
      'Tenant-wide calendar sync monitoring with mailbox-level diagnostics and a 30-day ' +
      "time-series chart. A new Microsoft 365 live feed surfaces real-time sync events as " +
      'they happen. Network blips no longer cascade into surprise logouts.',
    highlights: [
      {
        icon: 'rating',
        title: 'Tenant sync stats',
        text: 'Active mailboxes, never-synced, failed in the last 24h, stale anchors, success rate — at a glance, in one place.',
      },
      {
        icon: 'layers',
        title: 'Per-mailbox health + time series',
        text: 'A 30-day chart of created / updated / deleted runs colour-coded ok / warn / err, plus health signals for every mailbox.',
      },
      {
        icon: 'settings',
        title: 'Diagnose-mailbox flow',
        text: 'One click runs Graph reachability + anchor age checks and returns an actionable recommendation.',
      },
      {
        icon: 'recurring',
        title: 'Manual triggers',
        text: 'Trigger delta sync, renew webhooks, run a full backfill with configurable history depth, or restart backfill for a single mailbox.',
      },
      {
        icon: 'sparkles',
        title: 'Microsoft 365 Live feed',
        text: '/settings/integrations/microsoft/live shows a real-time sliding-card stream powered by graphql-ws subscription, with filter chips for ok / pending / err.',
      },
      {
        icon: 'plug',
        title: 'Asset Discovery',
        text: 'Connect cards for Microsoft Places, Logitech Sync, Neat Pulse, Archilogic and Yealink YMCS — the framework for every future cards / devices integration.',
      },
      {
        icon: 'star',
        title: 'Sessions survive network blips',
        text: 'Transient Apollo errors no longer cascade into surprise logouts; an apiOnline signal handles re-auth only when genuinely needed.',
      },
    ],
    tags: ['Integrations'],
  },
  {
    id: 'r-2026-05-19-a-desktop-and-analytics',
    date: '2026-05-19',
    title: 'Native desktop app + a typed analytics suite',
    summary:
      'A macOS desktop wrapper with native menus, hotkeys and notifications joins the family, ' +
      'and the in-app analytics suite is now 80+ typed events covering every key flow. The ' +
      'privacy toggle on /profile/privacy lets you opt out in one click.',
    highlights: [
      {
        icon: 'sparkles',
        title: 'macOS desktop app via Tauri',
        text: 'Single-instance lock, window-state persistence, OS Light/Dark sync, native menu bar and a console-to-log-file forwarder for support.',
      },
      {
        icon: 'recurring',
        title: 'Global hotkey from the background',
        text: 'Cmd+Shift+Space summons the command palette even when the app is minimised or hidden behind another window.',
      },
      {
        icon: 'star',
        title: 'Native file picker + notifications',
        text: 'Division logos, profile photos and feedback screenshots open native pickers; booking confirmations pop OS banners with a today-count dock badge.',
      },
      {
        icon: 'layers',
        title: 'Admin tabs cap raised to 8 on desktop',
        text: 'Up to 8 simultaneous admin tabs (vs 3 on web) with mod+1..8 activators — power-user-friendly when native windowing is available.',
      },
      {
        icon: 'rating',
        title: '80+ typed analytics events',
        text: 'Session lifecycle, viewer, search funnel, AI panels, command palette, BMP, onboarding, building alerts and homepage widget actions — all compile-time validated.',
      },
      {
        icon: 'settings',
        title: 'Privacy toggle at /profile/privacy',
        text: 'Opt out of product analytics in one click; session replay masks all inputs by default with opt-in .ph-mask for any extra fields you want hidden.',
      },
    ],
    tags: ['Desktop', 'Analytics'],
  },
  {
    id: 'r-2026-05-09-flow-projects',
    date: '2026-05-09',
    title: 'Flow Projects + cross-recording continuity',
    summary:
      'Group meetings into named projects and see context across every recording. ' +
      'Flow cards now surface sibling meetings from the same recurring series and from ' +
      'every project a meeting belongs to — so a weekly 1:1 or a multi-meeting initiative ' +
      'reads as one connected thread instead of unrelated standalone recordings.',
    highlights: [
      {
        icon: 'folder',
        title: 'Tag any meeting into a project',
        text: 'A new "+ Project" affordance on the meeting panel and on every Flow card — Granola-style inline picker with create-and-attach in one click.',
      },
      {
        icon: 'recurring',
        title: 'Recurring series back-fill',
        text: 'Tag a recurring 1:1 into a project and opt in — every prior occurrence joins automatically, future ones auto-attach as they arrive.',
      },
      {
        icon: 'layers',
        title: 'Projects list page + detail panel',
        text: 'A dedicated /projects route with cards for every project, plus a right-anchored detail panel with members, attached meetings and managed series rules.',
      },
      {
        icon: 'edit',
        title: 'Rich-text descriptions',
        text: 'Project descriptions support paragraphs, bullets, headings and links via the same editor used elsewhere in the app. Card summaries strip the markup for clean previews.',
      },
      {
        icon: 'sparkles',
        title: 'Visual feedback when adding to a project',
        text: 'Chip cluster pulses in, dropdown shows a "✓ Added to <name>" success state, and the Flow card continuity surface updates immediately.',
      },
    ],
    tags: ['Flow', 'New'],
  },
  {
    id: 'r-2026-05-09-onboarding-flow-step',
    date: '2026-05-09',
    title: 'Welcome tour: dedicated Flow step',
    summary:
      'A new step in the welcome tour explains what Flow is, what it does today, and where ' +
      "it's heading — Linear/Jira pulled into projects, missed-meeting auto-attach, " +
      'bring-your-own transcripts, meeting bots for Teams/Meet, and project-level AI synthesis. ' +
      'Includes an honest heads-up that the Flow desktop app is currently a private alpha.',
    highlights: [
      {
        icon: 'sparkles',
        title: 'New step format',
        text: 'Three blocks: "In the app today", "Coming next" (dashed-border future cards), and an amber heads-up note for the desktop-app rollout.',
      },
      {
        icon: 'star',
        title: 'New eyebrow pill',
        text: 'Brand-tinted "New" pill with a sparkles glyph signals fresh content in the tour without shouting.',
      },
      {
        icon: 'mic',
        title: 'Missing icon glyphs registered',
        text: 'mic, folder, plug, bot and brain glyphs landed in the central icon registry — the meeting panel was previously rendering empty SVGs for mic and folder.',
      },
    ],
    tags: ['Onboarding', 'Flow'],
  },
  {
    id: 'r-2026-04-30-themes-and-fonts',
    date: '2026-04-30',
    title: 'Themes, fonts and division branding',
    summary:
      'A multi-axis appearance system: 8 themes (4 light + 4 dark), 4 font families, and ' +
      "automatic colour derivation from your division's brand. Pick once on /profile/appearance " +
      'and everything from focus rings to project chips picks up the right tokens.',
    highlights: [
      {
        icon: 'sparkles',
        title: '8 themes, 4 fonts',
        text: 'Light row: Pure, Workplace Paper, Warm, Light. Dark row: Midnight, Indigo Night, Slate, Default. Pair with Inter, IBM Plex, Space Grotesk or Manrope.',
      },
      {
        icon: 'rating',
        title: 'Division-derived brand colour',
        text: "Your division's primary colour is auto-darkened or lightened to keep contrast safe in both modes, then propagated to every --color-primary usage.",
      },
      {
        icon: 'settings',
        title: 'Brand Test page (admin-only)',
        text: 'A dev-mode preview at /settings/brand-test renders 57 real division brands across the full component matrix to catch contrast regressions early.',
      },
    ],
    tags: ['Design system'],
  },
  {
    id: 'r-2026-04-28-home-redesign',
    date: '2026-04-28',
    title: 'Home redesigned around your day',
    summary:
      'A spotlight grid on the home page surfaces the next thing you need: today\'s next ' +
      'meeting, the article being lead-promoted, the spaces nearest you, and a one-click ' +
      'planner for the week ahead.',
    highlights: [
      {
        icon: 'calendar',
        title: 'Next-meeting widget',
        text: 'Surfaces the next meeting on your calendar with a one-tap join button and a quick link to the meeting panel.',
      },
      {
        icon: 'newspaper',
        title: 'Lead + Spotlight content',
        text: 'A lead promotion area for the most important article of the moment, plus a Spotlight grid of trending content rolled up across your division.',
      },
      {
        icon: 'zap',
        title: 'QuickBook week planner',
        text: 'Plan the next 7 days from the home page — see who else is in, find a desk near them, and book without opening the map.',
      },
    ],
    tags: ['Home'],
  },
];
